/**
 * VAT Dropdown Functionality
 * Handles VAT selection, local storage, and UI updates
 */

(function() {
  'use strict';

  const VAT_STORAGE_KEY = 'bentley_vat_setting';
  const TAX_EXEMPT_STORAGE_KEY = 'bentley_tax_exempt';
  const DEFAULT_VAT = 'inc';
  let VAT_RATE = 20; // Default VAT rate, will be overridden by theme setting

  /**
   * Sync cart items with current VAT preference.
   * Uses window.swapCartToVatExempt from cart-tax-exempt.js if available.
   * This ensures cart items match the user's VAT setting when navigating between pages.
   */
  function syncCartWithVatPreference() {
    // Wait for swapCartToVatExempt to be available (loaded from cart-tax-exempt.js)
    waitForSwapFunction(function() {
      doSyncCart();
    });
  }

  /**
   * Wait for the swap function to be available from cart-tax-exempt.js
   */
  function waitForSwapFunction(callback, attempts) {
    attempts = attempts || 0;
    var maxAttempts = 20; // Max 2 seconds (20 * 100ms)

    if (typeof window.swapCartToVatExempt === 'function') {
      callback();
      return;
    }

    if (attempts < maxAttempts) {
      setTimeout(function() {
        waitForSwapFunction(callback, attempts + 1);
      }, 100);
    }
  }

  function doSyncCart() {
    if (typeof window.swapCartToVatExempt !== 'function') {
      return;
    }

    var currentVat = localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
    var enableExempt = currentVat === 'ex';

    // Call the swap function from cart-tax-exempt.js
    // It will check if items need swapping based on current state
    var result = window.swapCartToVatExempt(enableExempt, '');

    // swapCartToVatExempt returns a Promise
    if (result && typeof result.then === 'function') {
      result.then(function() {
        // After swap, refresh VAT prices display
        if (typeof window.applyVatToPrices === 'function') {
          window.applyVatToPrices(currentVat);
        }
        if (typeof window.jQuery !== 'undefined') {
          window.jQuery('body').trigger('refreshCurrency');
        }
      }).catch(function(err) {
        // eslint-disable-next-line no-console
        console.error('Error syncing cart with VAT preference', err);
      });
    }
  }

  /**
   * Initialize VAT dropdown functionality
   */
  function initVatDropdown() {
    // Handle standalone VAT toggle box (if it exists)
    const vatToggleBox = document.querySelector('.vat-toggle-box');
    if (vatToggleBox) {
      initializeVatToggle(vatToggleBox);
    }

    // Handle VAT toggle in settings dropdown
    const vatToggleDropdowns = document.querySelectorAll('.vat-toggle-dropdown');
    vatToggleDropdowns.forEach(dropdown => {
      initializeVatToggleDropdown(dropdown);
    });
  }

  /**
   * Initialize standalone VAT toggle box
   */
  function initializeVatToggle(vatToggleBox) {
    // Get VAT rate from data attribute
    const vatRateAttr = vatToggleBox.getAttribute('data-vat-rate');
    if (vatRateAttr) {
      VAT_RATE = parseFloat(vatRateAttr);
    }

    const dropdownToggle = vatToggleBox.querySelector('.tt-dropdown-toggle');
    const dropdownMenu = vatToggleBox.querySelector('.tt-dropdown-menu');
    const vatOptions = vatToggleBox.querySelectorAll('.vat-option');

    if (!dropdownToggle || !dropdownMenu || vatOptions.length === 0) {
      return;
    }

    // Load saved VAT setting from local storage
    const savedVat = localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
    updateDropdownDisplay(dropdownToggle, savedVat);
    // Also highlight the active option in the dropdown
    updateStandaloneDropdownActiveState(vatToggleBox, savedVat);

    // Toggle dropdown on button click
    dropdownToggle.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = dropdownMenu.style.display !== 'none';
      dropdownMenu.style.display = isOpen ? 'none' : 'block';
    });

    // Handle VAT option selection
    vatOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const vatValue = this.getAttribute('data-vat');

        // Save to local storage (sync both VAT and tax exempt settings)
        localStorage.setItem(VAT_STORAGE_KEY, vatValue);
        localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, vatValue === 'ex' ? 'yes' : 'no');

        // Update display
        updateDropdownDisplay(dropdownToggle, vatValue);
        // Update active state highlighting
        updateStandaloneDropdownActiveState(vatToggleBox, vatValue);

        // Close dropdown
        dropdownMenu.style.display = 'none';

        // Dispatch custom event for other scripts to listen to
        const event = new CustomEvent('vatChanged', { detail: { vat: vatValue } });
        document.dispatchEvent(event);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
      if (!vatToggleBox.contains(e.target)) {
        dropdownMenu.style.display = 'none';
      }
    });

    // Prevent dropdown from closing when clicking inside it
    dropdownMenu.addEventListener('click', function(e) {
      e.stopPropagation();
    });
  }

  /**
   * Initialize VAT toggle in settings dropdown
   */
  function initializeVatToggleDropdown(dropdown) {
    // Get VAT rate from data attribute
    const vatRateAttr = dropdown.getAttribute('data-vat-rate');
    if (vatRateAttr) {
      VAT_RATE = parseFloat(vatRateAttr);
    }

    const vatOptions = dropdown.querySelectorAll('.vat-option');
    if (vatOptions.length === 0) {
      return;
    }

    // Load saved VAT setting from local storage
    const savedVat = localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
    updateSettingsDropdownDisplay(dropdown, savedVat);

    // Handle VAT option selection
    vatOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const vatValue = this.getAttribute('data-vat');

        // Save to local storage (sync both VAT and tax exempt settings)
        localStorage.setItem(VAT_STORAGE_KEY, vatValue);
        localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, vatValue === 'ex' ? 'yes' : 'no');

        // Update display in all VAT dropdowns
        updateSettingsDropdownDisplay(dropdown, vatValue);

        // Also update standalone toggle if it exists
        const standaloneToggle = document.querySelector('.vat-toggle-box .tt-dropdown-toggle');
        if (standaloneToggle) {
          updateDropdownDisplay(standaloneToggle, vatValue);
        }

        // Close the parent settings dropdown by triggering the close button
        const parentDropdown = dropdown.closest('.tt-dropdown-obj');
        if (parentDropdown) {
          const closeButton = parentDropdown.querySelector('.tt-mobile-add .tt-close');
          if (closeButton) {
            // Trigger click on close button to properly close the dropdown
            closeButton.click();
          }
        }

        // Dispatch custom event for other scripts to listen to
        const event = new CustomEvent('vatChanged', { detail: { vat: vatValue } });
        document.dispatchEvent(event);
      });
    });
  }

  /**
   * Update dropdown display text based on selected VAT
   * @param {HTMLElement} dropdownToggle - The dropdown toggle button
   * @param {string} vatValue - The VAT value ('ex' or 'inc')
   */
  function updateDropdownDisplay(dropdownToggle, vatValue) {
    const textSpan = dropdownToggle.querySelector('.tt-text');

    if (textSpan) {
      if (vatValue === 'inc') {
        textSpan.textContent = 'Inc. Vat';
      } else {
        textSpan.textContent = 'Ex. Vat';
      }
    }
  }

  /**
   * Update standalone dropdown active state to highlight selected VAT option
   * @param {HTMLElement} vatToggleBox - The VAT toggle box element
   * @param {string} vatValue - The VAT value ('ex' or 'inc')
   */
  function updateStandaloneDropdownActiveState(vatToggleBox, vatValue) {
    const vatOptions = vatToggleBox.querySelectorAll('.vat-option');

    vatOptions.forEach(option => {
      if (option.getAttribute('data-vat') === vatValue) {
        option.classList.add('active');
      } else {
        option.classList.remove('active');
      }
    });
  }

  /**
   * Update settings dropdown display to highlight selected VAT option
   * @param {HTMLElement} dropdown - The VAT toggle dropdown element
   * @param {string} vatValue - The VAT value ('ex' or 'inc')
   */
  function updateSettingsDropdownDisplay(dropdown, vatValue) {
    const vatOptions = dropdown.querySelectorAll('.vat-option');

    vatOptions.forEach(option => {
      if (option.getAttribute('data-vat') === vatValue) {
        option.classList.add('active');
      } else {
        option.classList.remove('active');
      }
    });
  }

  /**
   * Get the current VAT setting from local storage
   * @returns {string} The current VAT setting ('ex' or 'inc')
   */
  function getCurrentVatSetting() {
    return localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_VAT;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initVatDropdown();
      // Sync cart with VAT preference on every page load
      // This ensures cart items match the user's VAT setting (e.g., if they
      // selected VAT exempt on cart page, then navigated away and changed to Inc VAT)
      syncCartWithVatPreference();
    });
  } else {
    initVatDropdown();
    syncCartWithVatPreference();
  }

  // Listen for vatChanged events from other scripts (e.g., cart tax exempt dropdown)
  // to keep the header VAT toggle in sync
  document.addEventListener('vatChanged', function(e) {
    if (!e || !e.detail || !e.detail.vat) {
      return;
    }

    var vatValue = e.detail.vat;

    // Update standalone VAT toggle display if it exists
    var standaloneToggleBox = document.querySelector('.vat-toggle-box');
    if (standaloneToggleBox) {
      var standaloneToggle = standaloneToggleBox.querySelector('.tt-dropdown-toggle');
      if (standaloneToggle) {
        updateDropdownDisplay(standaloneToggle, vatValue);
      }
      updateStandaloneDropdownActiveState(standaloneToggleBox, vatValue);
    }

    // Update all settings dropdown displays
    var vatToggleDropdowns = document.querySelectorAll('.vat-toggle-dropdown');
    vatToggleDropdowns.forEach(function(dropdown) {
      updateSettingsDropdownDisplay(dropdown, vatValue);
    });

    // Sync cart items when VAT preference changes
    // Skip if we're on the cart page (cart-tax-exempt.js handles it there)
    if (window.location.pathname !== '/cart') {
      syncCartWithVatPreference();
    }
  });

  // Expose functions globally for external use
  window.getCurrentVatSetting = getCurrentVatSetting;
  window.syncCartWithVatPreference = syncCartWithVatPreference;
})();

